import { db } from '../config/firebase';
import { auth } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
  setDoc,
  getDoc
} from 'firebase/firestore';
import NotificationSettingsService from './NotificationSettingsService';

class NotificationService {
  // Make all methods static for consistency
  static async createNotification(data) {
    try {
      const notificationData = {
        ...data,
        read: false,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp() // Add this for consistency
      };

      const notificationRef = await addDoc(collection(db, 'notifications'), notificationData);
      return notificationRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  // Make this static
  static async createMessageNotification({
    receiverId,
    senderId,
    senderName,
    senderPhoto,
    message,
    conversationId,
    productId
  }) {
    try {
      if (!receiverId || !senderId || !message) {
        throw new Error('Missing required parameters for message notification');
      }

      // Check notification settings first
      const settingsDoc = await getDoc(doc(db, 'userSettings', receiverId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;

      // Strict checking of notification settings
      const notificationsEnabled = settings?.notifications?.enabled === true;
      const messagesEnabled = settings?.notifications?.messages === true;

      console.log('Checking notification settings for message:', {
        receiverId,
        notificationsEnabled,
        messagesEnabled,
        settings: settings?.notifications
      });

      // Only create notification if both settings are explicitly true
      if (!notificationsEnabled || !messagesEnabled) {
        console.log('Skipping message notification - settings disabled');
        return;
      }

      await addDoc(collection(db, 'notifications'), {
        type: 'message',
        receiverId,
        senderId,
        senderName: senderName || 'Anonymous',
        senderPhoto: senderPhoto || null,
        title: 'New Message',
        message,
        conversationId,
        productId,
        read: false,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating message notification:', error);
      throw error;
    }
  }

  static async createOrderNotification({
    receiverId,
    senderId,
    senderName,
    senderPhoto,
    orderId,
    productId,
    productTitle
  }) {
    try {
      if (!receiverId || !senderId || !orderId || !productTitle) {
        throw new Error('Missing required parameters for order notification');
      }

      // Check notification settings first
      const settingsDoc = await getDoc(doc(db, 'userSettings', receiverId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;

      // Strict checking of notification settings
      const notificationsEnabled = settings?.notifications?.enabled === true;
      const ordersEnabled = settings?.notifications?.orders === true;

      console.log('Checking notification settings for order:', {
        receiverId,
        notificationsEnabled,
        ordersEnabled,
        settings: settings?.notifications
      });

      // Only create notification if both settings are explicitly true
      if (!notificationsEnabled || !ordersEnabled) {
        console.log('Skipping order notification - settings disabled');
        return;
      }

      await addDoc(collection(db, 'notifications'), {
        type: 'order',
        receiverId,
        senderId,
        senderName: senderName || 'Anonymous',
        senderPhoto: senderPhoto || null,
        title: 'New Order',
        message: `New order for ${productTitle}`,
        orderId,
        productId,
        read: false,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating order notification:', error);
      throw error;
    }
  }

  // Make this static
  static async getNotifications(userId) {
    try {
      if (!userId) {
        console.error('No user ID provided');
        return [];
      }

      // First get user settings
      const settingsDoc = await getDoc(doc(db, 'userSettings', userId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;

      // Get base notifications query
      const notificationsRef = collection(db, 'notifications');
      let baseQuery = query(
        notificationsRef,
        where('receiverId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      // Get all notifications first
      const querySnapshot = await getDocs(baseQuery);
      
      // Filter notifications based on settings
      const notifications = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(notification => {
          // If no settings exist, don't show any notifications
          if (!settings?.notifications?.enabled) {
            return false;
          }

          // Filter based on notification type and corresponding setting
          switch (notification.type) {
            case 'message':
              return settings.notifications.messages === true;
            case 'order':
              return settings.notifications.orders === true;
            case 'marketing':
              return settings.notifications.marketing === true;
            default:
              return false;
          }
        });

      console.log('Filtered notifications based on settings:', {
        totalNotifications: querySnapshot.size,
        filteredCount: notifications.length,
        settings: settings?.notifications
      });

      return notifications;
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  }

  static async getUnreadCount(userId) {
    try {
      if (!userId) {
        console.error('No user ID provided');
        return 0;
      }

      // Get user settings first
      const settingsDoc = await getDoc(doc(db, 'userSettings', userId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;

      // Get all unread notifications
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('receiverId', '==', userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      
      // Filter notifications based on settings
      const count = querySnapshot.docs
        .filter(doc => {
          const notification = doc.data();
          
          // If notifications are disabled, don't count any
          if (!settings?.notifications?.enabled) {
            return false;
          }

          // Filter based on notification type and corresponding setting
          switch (notification.type) {
            case 'message':
              return settings.notifications.messages === true;
            case 'order':
              return settings.notifications.orders === true;
            case 'marketing':
              return settings.notifications.marketing === true;
            default:
              return false;
          }
        })
        .length;

      console.log('Filtered unread count:', {
        totalUnread: querySnapshot.size,
        filteredCount: count,
        settings: settings?.notifications
      });

      return count;
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  }

  // Make these static too
  static async markAsRead(notificationId) {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
        readPlatform: 'web',
        lastUpdated: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  static async handleNotificationClick(notification, navigate) {
    try {
      await this.markAsRead(notification.id);

      switch (notification.type) {
        case 'message':
          navigate('/profile', { 
            state: { 
              tabValue: 1,  // For messages tab
              from: 'notification'
            }
          });
          break;
        case 'order':
          navigate('/profile', { 
            state: { 
              tabValue: 2,  // For orders tab
              from: 'notification',
              orderTabValue: 1  // For received orders tab
            }
          });
          break;
        default:
          navigate('/profile');
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      throw error;
    }
  }

  static async markAllAsRead(type = null) {
    try {
      const batch = writeBatch(db);
      const notificationsRef = collection(db, 'notifications');
      
      let q = query(
        notificationsRef, 
        where('receiverId', '==', auth.currentUser.uid),
        where('read', '==', false)
      );
      
      if (type) {
        q = query(q, where('type', '==', type));
      }

      const querySnapshot = await getDocs(q);
      
      querySnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          read: true,
          readAt: serverTimestamp(),
          readPlatform: 'web',
          lastUpdated: serverTimestamp(),
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }
}

export default NotificationService; 